import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Box, Button, Card, CardActions, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import { useUser } from '../../../../contexts/user.context';
import { PLATFORMS, automaticPixStatus, paymentMethods, periodicityLabels, plans, subscriptionStatus } from '../../../../utils/constants';
import { formatIsoTimestamp, getSubscriptionStatusChip } from '../../../../utils/mappers';
import { isBilledByTrayCompatiblePlatform } from '../../../../utils/subscriptions';
import { mobilizeiSubscriptionsService } from '../../../../services/mobilizei/subscriptions.service';
import SubscriptionCancelDialog from './SubscriptionCancelDialog';
import { toast } from 'react-toastify';
import SubscriptionActivateDialog from './SubscriptionActivateDialog';

export default function Plan({ isExternalPayment }) {
    const { user, setUser } = useUser();
    const [plan, setPlan] = useState(null);

    const [isSubscriptionActivateDialogOpen, setIsSubscriptionActivateDialogOpen] = useState(false);
    const [isSubscriptionCancelDialogOpen, setIsSubscriptionCancelDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (user.subscription?.planId) {
            setPlan(plans[user.subscription.planId]);
        }
    }, []);

    const isAutomaticPix = user.subscription?.paymentMethod === paymentMethods.AUTOMATIC_PIX;
    const isAutomaticPixAuthorized = isAutomaticPix && user.subscription?.automaticPix?.status === automaticPixStatus.AUTHORIZED;

    const getStatusMessage = (status) => {
        switch (status) {
            case subscriptionStatus.PENDING:
                if (isAutomaticPix) {
                    return 'Pague a fatura em aberto e autorize o Pix Automático no app do seu banco para ativar a sua assinatura.'
                }
                return 'Efetue o pagamento da fatura em aberto para ativar a sua assinatura.'
            case subscriptionStatus.EXPIRED:
                if (isAutomaticPixAuthorized) {
                    return 'O débito automático da renovação da sua assinatura está sendo processado pelo Pix Automático.'
                }
                return 'Efetue o pagamento da fatura em aberto para evitar o cancelamento da sua assinatura.'
            case subscriptionStatus.PENDING_SUSPENSION:
                return `Sua assinatura foi cancelada e permanecerá ativa até o dia ${formatIsoTimestamp(user.subscription?.expiresAt, 'DD/MM/YYYY')}. Não serão geradas nenhuma nova fatura ou cobrança.`
            case subscriptionStatus.SUSPENDED:
                return 'Sua fatura foi cancelada e não serão geradas nenhuma nova fatura ou cobrança.'
            default:
                return '';
        }
    }

    const onCancelSubscription = () => {
        if (!user?.subscription?.id) {
            return
        }

        setIsSubscriptionCancelDialogOpen(false);
        setIsLoading(true);

        mobilizeiSubscriptionsService.suspend(user?.subscription?.id)
            .then(() => {
                toast.success('Assinatura cancelada com sucesso!');

                // Update the local user data to match the data that was 
                // updated on the backend
                setUser({
                    ...user, subscription: {
                        ...user?.subscription,
                        status: subscriptionStatus.PENDING_SUSPENSION
                    }
                });
            })
            .catch(error => {
                const errorCode = _.get(error, 'error.code', '');
                toast.error(`Não foi possível cancelar a assinatura. Por favor, tente novamente mais tarde. (#${errorCode})`);
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const onActivateSubscription = () => {
        if (!user?.subscription?.id) {
            return
        }

        setIsSubscriptionActivateDialogOpen(false);
        setIsLoading(true);

        mobilizeiSubscriptionsService.activate(user?.subscription?.id)
            .then(() => {
                toast.success(isAutomaticPix ?
                    'Assinatura reativada com sucesso! Na próxima renovação você receberá um novo QR Code para autorizar o Pix Automático novamente.' :
                    'Assinatura reativada com sucesso!'
                );

                // Update the local user data to match the data that was 
                // updated on the backend
                setUser({
                    ...user, subscription: {
                        ...user?.subscription,
                        status: subscriptionStatus.ACTIVE
                    }
                });
            })
            .catch(error => {
                const errorCode = _.get(error, 'error.code', '');
                toast.error(`Não foi possível reativar a assinatura. Por favor, tente novamente mais tarde. (#${errorCode})`);
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    return (
        <Box display='flex' flexDirection='column' width='100%'>
            <Typography variant="h5" mb={2}>
                Sua assinatura
            </Typography>

            {isLoading ? (
                <Card
                    elevation={2}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        justifyContent: 'center',
                        padding: 1
                    }}
                >
                    <CircularProgress />
                </Card>
            ) : (
                <Card
                    elevation={2}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        justifyContent: 'center',
                        padding: 1
                    }}
                >
                    <CardContent sx={{ paddingX: 4 }}>
                        <Grid container direction="row" spacing={1}>
                            <Grid item xs={5} sm={6}>
                                <Typography variant='body1'>
                                    Status
                                </Typography>
                            </Grid>
                            <Grid item xs={7} sm={6}>
                                <Typography variant='body1' color={'grey'}>
                                    {getSubscriptionStatusChip(user.subscription?.status)}
                                </Typography>
                            </Grid>

                            <Grid item xs={5} sm={6}>
                                <Typography variant='body1'>
                                    Plano
                                </Typography>
                            </Grid>
                            <Grid item xs={7} sm={6}>
                                <Typography variant='body1' color={'grey'}>
                                    {plan?.name || '-'}
                                </Typography>
                            </Grid>

                            {!isExternalPayment &&
                                <>
                                    <Grid item xs={5} sm={6}>
                                        <Typography variant='body1'>
                                            Valor
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={7} sm={6}>
                                        <Typography variant='body1' color={'grey'}>
                                            {plan?.totalPrice ?
                                                `R$ ${plan?.totalPrice},00 / ${periodicityLabels[plan?.periodicity]?.unit || 'mês'}` :
                                                '-'
                                            }
                                        </Typography>
                                    </Grid>
                                </>
                            }

                            {!isExternalPayment && user.subscription?.status === subscriptionStatus.ACTIVE &&
                                <>
                                    <Grid item xs={5} sm={6}>
                                        <Typography variant='body1'>
                                            Próxima cobrança
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={7} sm={6}>
                                        <Typography variant='body1' color={'grey'}>
                                            {formatIsoTimestamp(user.subscription?.expiresAt, 'DD/MM/YYYY') || '-'}
                                        </Typography>
                                    </Grid>
                                </>
                            }

                            <Grid item xs={12}>
                                <Typography variant='body1' color={'grey'} fontStyle={'italic'}>
                                    {getStatusMessage(user.subscription?.status)}
                                </Typography>
                            </Grid>

                            {user.subscription?.isAlsoChargedOnTray && user.store?.platformId === PLATFORMS.TRAY &&
                                <Grid item xs={12}>
                                    <Typography variant='body1' color={'grey'} fontStyle={'italic'}>
                                        Valores adicionais podem ser cobrados diretamente pela Tray, junto com a fatura da
                                        sua loja na plataforma. Acesse a área de{' '}
                                        <a
                                            href={`http://api.minhaassinatura.tray.com.br/api/auth?store=${user.store?.id}&url=${user.store?.storeUrl}`}
                                            target='_blank'
                                        >
                                            Histórico de Faturas
                                        </a>
                                        {' '}do painel administrativo da Tray para ver mais detalhes.
                                    </Typography>
                                </Grid>
                            }

                            {user.store?.platformId === PLATFORMS.TRAY && isBilledByTrayCompatiblePlatform(user.subscription) &&
                                <Grid item xs={12}>
                                    <Typography variant='body1' color={'grey'} fontStyle={'italic'}>
                                        O valor mensal da sua assinatura Mobilizei é cobrado diretamente pela Tray, junto com a
                                        fatura da sua loja na plataforma. Acesse a área de{' '}
                                        <a
                                            href={`http://api.minhaassinatura.tray.com.br/api/auth?store=${user.store?.id}&url=${user.store?.storeUrl}`}
                                            target='_blank'
                                        >
                                            Histórico de Faturas
                                        </a>
                                        {' '}do painel administrativo da Tray para ver mais detalhes.
                                    </Typography>
                                </Grid>
                            }

                            {user.store?.platformId === PLATFORMS.BAGY && isBilledByTrayCompatiblePlatform(user.subscription) &&
                                <Grid item xs={12}>
                                    <Typography variant='body1' color={'grey'} fontStyle={'italic'}>
                                        O valor mensal da sua assinatura Mobilizei é cobrado diretamente pela Bagy, junto com a
                                        fatura da sua loja na plataforma.
                                    </Typography>
                                </Grid>
                            }

                            {user.store?.platformId === PLATFORMS.WIX &&
                                <Grid item xs={12}>
                                    <Typography variant='body1' color={'grey'} fontStyle={'italic'}>
                                        O valor mensal da sua assinatura Mobilizei é cobrado diretamente pela Wix. Acesse a área de{' '}
                                        <a
                                            href={`https://manage.wix.com/studio/subscriptions`}
                                            target='_blank'
                                        >
                                            Assinaturas
                                        </a>
                                        {' '}do painel administrativo da Wix para ver mais detalhes.
                                    </Typography>
                                </Grid>
                            }

                        </Grid>
                    </CardContent>
                    <CardActions disableSpacing sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        padding: 0
                    }}>

                        {!isExternalPayment && (user.subscription?.status === subscriptionStatus.ACTIVE ||
                            user.subscription?.status === subscriptionStatus.EXPIRED) &&
                            <Button onClick={() => setIsSubscriptionCancelDialogOpen(true)} size="small" sx={{
                                color: 'red',
                                ':hover': { bgcolor: 'transparent' }
                            }}>
                                Cancelar
                            </Button>
                        }

                        {!isExternalPayment && user.subscription?.status === subscriptionStatus.PENDING_SUSPENSION &&
                            <Button onClick={() => setIsSubscriptionActivateDialogOpen(true)} size="small" sx={{
                                color: 'red',
                                ':hover': { bgcolor: 'transparent' }
                            }}>
                                Reativar
                            </Button>
                        }

                    </CardActions>
                </Card>
            )}

            <SubscriptionCancelDialog
                isOpen={isSubscriptionCancelDialogOpen}
                setIsOpen={setIsSubscriptionCancelDialogOpen}
                onConfirm={() => onCancelSubscription()}
                expiresAt={formatIsoTimestamp(user.subscription?.expiresAt, 'DD/MM/YYYY')}
                isAutomaticPix={isAutomaticPix}
            />

            <SubscriptionActivateDialog
                isOpen={isSubscriptionActivateDialogOpen}
                setIsOpen={setIsSubscriptionActivateDialogOpen}
                onConfirm={() => onActivateSubscription()}
            />
        </Box>
    );
}